import React from "react";
import {
  ColumnDef,
  Row,
  SortingState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { TableHeaderSortIcon } from "components/Common/Table/TableHeaderSortIcon";

interface IProps<T extends object> {
  data: T[];
  columns: ColumnDef<T, any>[];
  onRowClick?: (row: Row<T>["original"]) => void;
}

export const Table = <T extends object>({ data, columns, onRowClick }: IProps<T>) => {
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const table = useReactTable({
    data,
    columns,
    defaultColumn: {
      size: 200, //starting column size
      minSize: 50, //enforced during column resizing
      maxSize: 500, //enforced during column resizing
    },
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    columnResizeMode: "onChange",
    columnResizeDirection: "ltr",
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <div className="overflow-x-auto rounded-lg">
      <table className="w-full">
        <thead className="h-10">
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th
                  className={`text-white px-4 py-2 bg-breedr-blue-500 text-left text-xs uppercase border-r-gray-600 border-solid border-r align-middle ${
                    header.column.getIsSorted() ? "text-breedr-yellow-500" : ""
                  }`}
                  key={header.id}
                  colSpan={header.colSpan}
                  onClick={header.column.getToggleSortingHandler()}
                >
                  <div className="flex flex-row justify-between align-middle">
                    {flexRender(header.column.columnDef.header, header.getContext())}
                    {{
                      asc: <TableHeaderSortIcon sorted={true} sortedDesc={false} />,
                      desc: <TableHeaderSortIcon sorted={true} sortedDesc={true} />,
                    }[header.column.getIsSorted() as string] ?? null}
                  </div>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {table.getRowModel().rows.map((row) => (
            <tr
              className={onRowClick ? "cursor-pointer" : "cursor-auto"}
              key={row.id}
              onClick={onRowClick ? () => onRowClick(row.original) : undefined}
            >
              {row.getVisibleCells().map((cell) => (
                <td
                  className="px-4 py-2 bg-white text-sm text-ellipsis overflow-hidden border-b-gray-200 border-solid border-b align-middle h-12"
                  key={cell.id}
                  style={{
                    width: cell.column.getSize(),
                  }}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
